import { useEffect, useRef, useState } from 'react'
import { apiBlob } from './api'

/**
 * Layout that differs structurally (the wizard's sidebar stepper versus its
 * rail) is chosen in JS rather than rendered twice and hidden with CSS —
 * two `<nav>`s with the same content is two entries in a screen reader's
 * landmark list.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  )

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const list = window.matchMedia(query)
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches)
    setMatches(list.matches)
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/**
 * Moves focus to a heading on mount. Route changes and wizard step changes
 * both land on their `<h1>`/`<h2>` (DESIGN.md §6.3), which is what makes a
 * keyboard or screen-reader user aware the page changed underneath them.
 */
export function useFocusOnMount<T extends HTMLElement>(deps: unknown[] = []) {
  const ref = useRef<T>(null)
  useEffect(() => {
    ref.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  return ref
}

/** Page title. Stated outcomes, not "Living Will Portal" on every screen. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    document.title = `${title} · Living Will Portal`
  }, [title])
}

/**
 * Fetches an authenticated PDF and hands back an object URL.
 *
 * Needed because `<iframe src>` and `<a download href>` cannot carry an
 * `Authorization` header, and every document endpoint requires one.
 */
export async function fetchDocumentUrl(path: string, signal?: AbortSignal): Promise<string> {
  const blob = await apiBlob(path, { signal })
  return URL.createObjectURL(blob)
}

/**
 * The same thing for a document that has to be *rendered* from a request body
 * rather than fetched by id — the admin template preview, where the draft
 * being previewed has not been saved anywhere yet.
 *
 * Callers own the returned URL and must revoke it.
 */
export async function renderDocumentUrl(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<string> {
  const blob = await apiBlob(path, { method: 'POST', body, signal })
  return URL.createObjectURL(blob)
}

/** Triggers a browser download of an authenticated document. */
export async function downloadDocument(path: string, filename: string): Promise<void> {
  const url = await fetchDocumentUrl(path)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // Give the browser a moment to start the download before the blob dies.
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

/** `document.body` scroll lock while a modal is open. */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [active])
}
